import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectOption } from '../ui/select';
import { Button } from '../ui/button';
import { FileText, Plus, Trash2 } from 'lucide-react';
import { isMobile, handleNumberInput, formatCurrency } from '../../lib/utils';

const ExpensesStep = ({ formData, setFormData, calculateAmounts }) => {

  const handleNestedInputChange = (parent, field, value) => {
    setFormData(prev => ({
      ...prev,
      [parent]: {
        ...prev[parent],
        [field]: value
      }
    }));
  };

  // Helper function to get company fee data
  const getCompanyFee = () => {
    try {
      return typeof formData.company_fee === 'string' ? JSON.parse(formData.company_fee) : formData.company_fee;
    } catch (e) {
      return { type: 'percentage', percentage: 0, amount: 0, distribution_type: 'percentage', installment_distribution: [] };
    }
  };

  // Helper function to get payment schedule
  const getPaymentSchedule = () => {
    return formData.payment_schedule || [];
  };

  // Add installment distribution
  const addInstallmentDistribution = () => {
    const companyFee = getCompanyFee();
    const paymentSchedule = getPaymentSchedule();
    const installment_distribution = companyFee.installment_distribution || [];
    
    // Find next available installment
    const usedInstallments = installment_distribution.map(dist => dist.installment);
    const nextInstallment = paymentSchedule.find(payment => !usedInstallments.includes(payment.installment_number || payment.installment));
    
    if (nextInstallment) {
      const newDistribution = {
        installment: nextInstallment.installment_number || nextInstallment.installment,
        percentage: 0,
        amount: 0
      };
      
      setFormData(prev => ({
        ...prev,
        company_fee: {
          ...companyFee,
          installment_distribution: [...installment_distribution, newDistribution]
        }
      }));
    }
  };

  // Remove installment distribution
  const removeInstallmentDistribution = (index) => {
    const companyFee = getCompanyFee();
    const installment_distribution = companyFee.installment_distribution || [];
    
    setFormData(prev => ({
      ...prev,
      company_fee: {
        ...companyFee,
        installment_distribution: installment_distribution.filter((_, i) => i !== index)
      }
    }));
  };

  // Update installment distribution
  const updateInstallmentDistribution = (index, field, value) => {
    const companyFee = getCompanyFee();
    const installment_distribution = companyFee.installment_distribution || [];
    
    const updatedDistribution = installment_distribution.map((dist, i) => 
      i === index ? { ...dist, [field]: value } : dist
    );
    
    setFormData(prev => ({
      ...prev,
      company_fee: {
        ...companyFee,
        installment_distribution: updatedDistribution
      }
    }));
  };

  // Helper function to get sales commission data
  const getSalesCommission = () => {
    try {
      return typeof formData.sales_commission === 'string' ? JSON.parse(formData.sales_commission) : formData.sales_commission;
    } catch (e) {
      return { type: 'percentage', percentage: 0, amount: 0, distribution_type: 'percentage', installment_distribution: [] };
    }
  };

  // Add sales commission installment distribution
  const addSalesCommissionDistribution = () => {
    const salesCommission = getSalesCommission();
    const paymentSchedule = getPaymentSchedule();
    const installment_distribution = salesCommission.installment_distribution || [];
    
    // Find next available installment
    const usedInstallments = installment_distribution.map(dist => dist.installment);
    const nextInstallment = paymentSchedule.find(payment => !usedInstallments.includes(payment.installment_number || payment.installment));
    
    if (nextInstallment) {
      const newDistribution = {
        installment: nextInstallment.installment_number || nextInstallment.installment,
        percentage: 0,
        amount: 0
      };
      
      setFormData(prev => ({
        ...prev,
        sales_commission: {
          ...salesCommission,
          installment_distribution: [...installment_distribution, newDistribution]
        }
      }));
    }
  };

  // Remove sales commission installment distribution
  const removeSalesCommissionDistribution = (index) => {
    const salesCommission = getSalesCommission();
    const installment_distribution = salesCommission.installment_distribution || [];
    
    setFormData(prev => ({
      ...prev,
      sales_commission: {
        ...salesCommission,
        installment_distribution: installment_distribution.filter((_, i) => i !== index)
      }
    }));
  };

  // Update sales commission installment distribution
  const updateSalesCommissionDistribution = (index, field, value) => {
    const salesCommission = getSalesCommission();
    const installment_distribution = salesCommission.installment_distribution || [];
    
    const updatedDistribution = installment_distribution.map((dist, i) => 
      i === index ? { ...dist, [field]: value } : dist
    );
    
    setFormData(prev => ({
      ...prev,
      sales_commission: {
        ...salesCommission,
        installment_distribution: updatedDistribution
      }
    }));
  };


  return (
    <div className="space-y-6">
      <div className="text-center mb-6">
        <div className="inline-flex items-center justify-center w-12 h-12 bg-orange-100 rounded-full mb-3">
          <FileText className="h-6 w-6 text-orange-600" />
        </div>
        <h2 className="text-2xl font-bold text-foreground mb-1">ค่าใช้จ่าย</h2>
        <p className="text-muted-foreground text-sm">จัดการค่าใช้จ่ายบริษัทและโปรเจค</p>
      </div>

      {/* Company Fee */}
      <Card className="border-2 border-green-200 hover:border-green-300 transition-colors">
        <CardHeader className="bg-green-50 py-3">
          <CardTitle className="flex items-center gap-2 text-green-800">
            <FileText className="h-5 w-5" />
            ค่าดำเนินการบริษัท
          </CardTitle>
        </CardHeader>
        <CardContent className="p-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <Label htmlFor="company_fee_type">ประเภทการคำนวณ</Label>
              <Select
                value={(() => {
                  try {
                    const companyFee = typeof formData.company_fee === 'string' ? JSON.parse(formData.company_fee) : formData.company_fee;
                    return companyFee?.type || 'percentage';
                  } catch (e) {
                    return 'percentage';
                  }
                })()}
                onChange={(e) => handleNestedInputChange('company_fee', 'type', e.target.value)}
              >
                <SelectOption value="percentage">เปอร์เซ็นต์</SelectOption>
                <SelectOption value="amount">จำนวนเงิน</SelectOption>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="company_fee_value">
                {(() => {
                  try {
                    const companyFee = typeof formData.company_fee === 'string' ? JSON.parse(formData.company_fee) : formData.company_fee;
                    return companyFee?.type === 'percentage' ? 'เปอร์เซ็นต์ (%)' : 'จำนวนเงิน';
                  } catch (e) {
                    return 'เปอร์เซ็นต์ (%)';
                  }
                })()}
              </Label>
              <Input
                id="company_fee_value"
                type={isMobile() ? "number" : "text"}
                placeholder={(() => {
                  try {
                    const companyFee = typeof formData.company_fee === 'string' ? JSON.parse(formData.company_fee) : formData.company_fee;
                    return companyFee?.type === 'percentage' ? '10' : '50000';
                  } catch (e) {
                    return '10';
                  }
                })()}
                value={(() => {
                  try {
                    const companyFee = typeof formData.company_fee === 'string' ? JSON.parse(formData.company_fee) : formData.company_fee;
                    return companyFee?.type === 'percentage' ? (companyFee?.percentage || 0) : (companyFee?.amount || 0);
                  } catch (e) {
                    return 0;
                  }
                })()}
                onChange={(e) => {
                  const value = parseFloat(e.target.value) || 0;
                  if ((() => {
                    try {
                      const companyFee = typeof formData.company_fee === 'string' ? JSON.parse(formData.company_fee) : formData.company_fee;
                      return companyFee?.type === 'percentage';
                    } catch (e) {
                      return true;
                    }
                  })()) {
                    handleNestedInputChange('company_fee', 'percentage', value);
                  } else {
                    handleNestedInputChange('company_fee', 'amount', value);
                  }
                }}
                onKeyDown={(e) => !isMobile() && handleNumberInput(e, (e) => {
                  const value = parseFloat(e.target.value) || 0;
                  if ((() => {
                    try {
                      const companyFee = typeof formData.company_fee === 'string' ? JSON.parse(formData.company_fee) : formData.company_fee;
                      return companyFee?.type === 'percentage';
                    } catch (e) {
                      return true;
                    }
                  })()) {
                    handleNestedInputChange('company_fee', 'percentage', value);
                  } else {
                    handleNestedInputChange('company_fee', 'amount', value);
                  }
                })}
                className="h-10"
              />
              <p className="text-sm text-muted-foreground">
                จำนวน: {formatCurrency(
                  (() => {
                    try {
                      const companyFee = typeof formData.company_fee === 'string' ? JSON.parse(formData.company_fee) : formData.company_fee;
                      return companyFee?.type === 'percentage';
                    } catch (e) {
                      return true;
                    }
                  })() 
                    ? (calculateAmounts().finalAmount * (() => {
                        try {
                          const companyFee = typeof formData.company_fee === 'string' ? JSON.parse(formData.company_fee) : formData.company_fee;
                          return companyFee?.percentage || 0;
                        } catch (e) {
                          return 0;
                        }
                      })()) / 100
                    : (() => {
                        try {
                          const companyFee = typeof formData.company_fee === 'string' ? JSON.parse(formData.company_fee) : formData.company_fee;
                          return companyFee?.amount || 0;
                        } catch (e) {
                          return 0;
                        }
                      })()
                )}
              </p>
            </div>
          </div>
          
          {/* Installment Distribution */}
          <div className="mt-6 pt-4 border-t border-green-200">
            <div className="flex items-center justify-between mb-4">
              <Label className="text-sm font-medium text-green-700">การกระจายค่าบริการตามงวด</Label>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={addInstallmentDistribution}
                className="text-green-600 border-green-300 hover:bg-green-50"
              >
                <Plus className="h-4 w-4 mr-1" />
                เพิ่มงวด
              </Button>
            </div>
            
            {getCompanyFee().installment_distribution && getCompanyFee().installment_distribution.length > 0 ? (
              <div className="space-y-3">
                {getCompanyFee().installment_distribution.map((dist, index) => (
                  <div key={index} className="p-3 bg-green-50 rounded-lg border border-green-200">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                      <div>
                        <Label className="text-xs text-green-700">งวดที่</Label>
                        <Input
                          type="number"
                          value={dist.installment || ''}
                          onChange={(e) => updateInstallmentDistribution(index, 'installment', parseInt(e.target.value) || 0)}
                          className="h-8 text-sm"
                          min="1"
                        />
                      </div>
                      <div>
                        <Label className="text-xs text-green-700">เปอร์เซ็นต์ (%)</Label>
                        <Input
                          type="number"
                          value={dist.percentage || ''}
                          onChange={(e) => updateInstallmentDistribution(index, 'percentage', parseFloat(e.target.value) || 0)}
                          className="h-8 text-sm"
                          min="0"
                          max="100"
                          step="0.01"
                        />
                      </div>
                      <div className="flex items-end">
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => removeInstallmentDistribution(index)}
                          className="text-red-600 border-red-300 hover:bg-red-50 h-8"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                    <div className="mt-2 text-xs text-green-600">
                      จำนวน: {formatCurrency(
                        getCompanyFee().type === 'percentage' 
                          ? (calculateAmounts().finalAmount * (getCompanyFee().percentage || 0) / 100) * (dist.percentage || 0) / 100
                          : (getCompanyFee().amount || 0) * (dist.percentage || 0) / 100
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-4 text-sm text-muted-foreground">
                ยังไม่ได้กำหนดการกระจายค่าบริการตามงวด
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Sales Commission */}
      <Card className="border-2 border-purple-200 hover:border-purple-300 transition-colors">
        <CardHeader className="bg-purple-50 py-3">
          <CardTitle className="flex items-center gap-2 text-purple-800">
            <FileText className="h-5 w-5" />
            Commission Sale
          </CardTitle>
        </CardHeader>
        <CardContent className="p-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <Label htmlFor="sales_commission_type">ประเภทการคำนวณ</Label>
              <Select
                value={(() => {
                  try {
                    const salesCommission = typeof formData.sales_commission === 'string' ? JSON.parse(formData.sales_commission) : formData.sales_commission;
                    return salesCommission?.type || 'percentage';
                  } catch (e) {
                    return 'percentage';
                  }
                })()}
                onChange={(e) => handleNestedInputChange('sales_commission', 'type', e.target.value)}
              >
                <SelectOption value="percentage">เปอร์เซ็นต์</SelectOption>
                <SelectOption value="amount">จำนวนเงิน</SelectOption>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="sales_commission_value">
                {(() => {
                  try {
                    const salesCommission = typeof formData.sales_commission === 'string' ? JSON.parse(formData.sales_commission) : formData.sales_commission;
                    return salesCommission?.type === 'percentage' ? 'เปอร์เซ็นต์ (%)' : 'จำนวนเงิน';
                  } catch (e) {
                    return 'เปอร์เซ็นต์ (%)';
                  }
                })()}
              </Label>
              <Input
                id="sales_commission_value"
                type={isMobile() ? "number" : "text"}
                placeholder={(() => {
                  try {
                    const salesCommission = typeof formData.sales_commission === 'string' ? JSON.parse(formData.sales_commission) : formData.sales_commission;
                    return salesCommission?.type === 'percentage' ? '5' : '25000';
                  } catch (e) {
                    return '5';
                  }
                })()}
                value={(() => {
                  try {
                    const salesCommission = typeof formData.sales_commission === 'string' ? JSON.parse(formData.sales_commission) : formData.sales_commission;
                    return salesCommission?.type === 'percentage' ? (salesCommission?.percentage || 0) : (salesCommission?.amount || 0);
                  } catch (e) {
                    return 0;
                  }
                })()}
                onChange={(e) => {
                  const value = parseFloat(e.target.value) || 0;
                  if ((() => {
                    try {
                      const salesCommission = typeof formData.sales_commission === 'string' ? JSON.parse(formData.sales_commission) : formData.sales_commission;
                      return salesCommission?.type === 'percentage';
                    } catch (e) {
                      return true;
                    }
                  })()) {
                    handleNestedInputChange('sales_commission', 'percentage', value);
                  } else {
                    handleNestedInputChange('sales_commission', 'amount', value);
                  }
                }}
                onKeyDown={(e) => !isMobile() && handleNumberInput(e, (e) => {
                  const value = parseFloat(e.target.value) || 0;
                  if ((() => {
                    try {
                      const salesCommission = typeof formData.sales_commission === 'string' ? JSON.parse(formData.sales_commission) : formData.sales_commission;
                      return salesCommission?.type === 'percentage';
                    } catch (e) {
                      return true;
                    }
                  })()) {
                    handleNestedInputChange('sales_commission', 'percentage', value);
                  } else {
                    handleNestedInputChange('sales_commission', 'amount', value);
                  }
                })}
                className="h-10"
              />
              <p className="text-sm text-muted-foreground">
                จำนวน: {formatCurrency(
                  (() => {
                    try {
                      const salesCommission = typeof formData.sales_commission === 'string' ? JSON.parse(formData.sales_commission) : formData.sales_commission;
                      return salesCommission?.type === 'percentage';
                    } catch (e) {
                      return true;
                    }
                  })() 
                    ? (calculateAmounts().finalAmount * (() => {
                        try {
                          const salesCommission = typeof formData.sales_commission === 'string' ? JSON.parse(formData.sales_commission) : formData.sales_commission;
                          return salesCommission?.percentage || 0;
                        } catch (e) {
                          return 0;
                        }
                      })()) / 100
                    : (() => {
                        try {
                          const salesCommission = typeof formData.sales_commission === 'string' ? JSON.parse(formData.sales_commission) : formData.sales_commission;
                          return salesCommission?.amount || 0;
                        } catch (e) {
                          return 0;
                        }
                      })()
                )}
              </p>
            </div>
          </div>
          
          {/* Sales Commission Installment Distribution */}
          <div className="mt-6 pt-4 border-t border-purple-200">
            <div className="flex items-center justify-between mb-4">
              <Label className="text-sm font-medium text-purple-700">การกระจาย Commission ตามงวด</Label>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={addSalesCommissionDistribution}
                className="text-purple-600 border-purple-300 hover:bg-purple-50"
              >
                <Plus className="h-4 w-4 mr-1" />
                เพิ่มงวด
              </Button>
            </div>
            
            {getSalesCommission().installment_distribution && getSalesCommission().installment_distribution.length > 0 ? (
              <div className="space-y-3">
                {getSalesCommission().installment_distribution.map((dist, index) => (
                  <div key={index} className="p-3 bg-purple-50 rounded-lg border border-purple-200">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                      <div>
                        <Label className="text-xs text-purple-700">งวดที่</Label>
                        <Input
                          type="number"
                          value={dist.installment || ''}
                          onChange={(e) => updateSalesCommissionDistribution(index, 'installment', parseInt(e.target.value) || 0)}
                          className="h-8 text-sm"
                          min="1"
                        />
                      </div>
                      <div>
                        <Label className="text-xs text-purple-700">เปอร์เซ็นต์ (%)</Label>
                        <Input
                          type="number"
                          value={dist.percentage || ''}
                          onChange={(e) => updateSalesCommissionDistribution(index, 'percentage', parseFloat(e.target.value) || 0)}
                          className="h-8 text-sm"
                          min="0"
                          max="100"
                          step="0.01"
                        />
                      </div>
                      <div className="flex items-end">
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => removeSalesCommissionDistribution(index)}
                          className="text-red-600 border-red-300 hover:bg-red-50 h-8"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                    <div className="mt-2 text-xs text-purple-600">
                      จำนวน: {formatCurrency(
                        getSalesCommission().type === 'percentage' 
                          ? (calculateAmounts().finalAmount * (getSalesCommission().percentage || 0) / 100) * (dist.percentage || 0) / 100
                          : (getSalesCommission().amount || 0) * (dist.percentage || 0) / 100
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-4 text-sm text-muted-foreground">
                ยังไม่ได้กำหนดการกระจาย Commission ตามงวด
              </div>
            )}
          </div>
        </CardContent>
      </Card>

    </div>
  );
};

export default ExpensesStep;
